import { Link } from "react-router-dom";
import { FlaskConical, Leaf } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CredibilityBadge {
  id: string;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  tooltip: string;
  ariaLabel: string;
}

const BADGES: CredibilityBadge[] = [
  {
    id: "science",
    icon: FlaskConical,
    title: "Testes clínicos IPclin",
    subtitle: "HRIPT n=60 · Fototeste n=30",
    tooltip:
      "Testes de segurança dermatológica conduzidos pelo IPclin (Jundiaí-SP): HRIPT com 60 participantes e Fototeste com 30 participantes",
    ariaLabel:
      "Selo de testes clínicos: HRIPT com 60 participantes e Fototeste com 30 participantes, conduzidos pelo IPclin",
  },
  {
    id: "ethical",
    icon: Leaf,
    title: "Cruelty-free",
    subtitle: "Sem testes em animais",
    tooltip: "Nenhum produto NAT HOF é testado em animais",
    ariaLabel:
      "Selo ético: cruelty-free, sem testes em animais",
  },
];

interface CredibilityBadgesProps {
  variant?: "footer" | "pdp" | "compact";
  className?: string;
}

export function CredibilityBadges({
  variant = "footer",
  className,
}: CredibilityBadgesProps) {
  const isCompact = variant === "compact";

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={cn(
          "flex flex-wrap items-center gap-2.5",
          variant === "footer" && "justify-center md:justify-start",
          variant === "pdp" && "max-w-xl",
          isCompact && "justify-start",
          className,
        )}
      >
        {BADGES.map((badge) => {
          const Icon = badge.icon;
          const isPdp = variant === "pdp";
          const badgeInner = (
            <>
              <Icon
                className="h-4 w-4 text-primary flex-shrink-0"
                aria-hidden="true"
              />
              {!isCompact && (
                <span className="flex flex-col leading-tight text-left">
                  <span className="text-xs font-semibold text-brand-deep">
                    {badge.title}
                  </span>
                  <span className="text-[10px] text-foreground/60">
                    {badge.subtitle}
                  </span>
                </span>
              )}
            </>
          );
          const baseClass = cn(
            "inline-flex items-center gap-2.5 bg-background border border-primary/15 rounded-lg shadow-sm transition-all duration-200 outline-none",
            "hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary/40",
            isPdp ? "cursor-pointer" : "cursor-default",
            isCompact ? "p-2" : "px-4 py-2.5",
          );
          return (
            <Tooltip key={badge.id}>
              <TooltipTrigger asChild>
                {isPdp ? (
                  // On PDP the badges are clickable and lead to the guarantee page
                  // (fixes dead clicks on the trust seals).
                  <Link
                    to="/garantia"
                    aria-label={`${badge.ariaLabel} — ver garantia de satisfação`}
                    className={baseClass}
                  >
                    {badgeInner}
                  </Link>
                ) : (
                  <div
                    role="img"
                    aria-label={badge.ariaLabel}
                    tabIndex={0}
                    className={baseClass}
                  >
                    {badgeInner}
                  </div>
                )}
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-center">
                <p className="font-semibold">{badge.title}</p>
                <p className="text-xs opacity-90">{badge.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
